import { Alert, Box, Button, Typography, useTheme } from "@mui/material";
import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
    BarChart,
    HorizontalBarChart,
    PieChart,
} from "../../components/Charts";
import { Card, CardContainer } from "../../components/Card";
import ApexCharts from "react-apexcharts";

const OverlapData = ({ data, onEdit }) => {
    const theme = useTheme();
    const totalFollowers = data?.total_follower || 0;
    const uniqueFollowers = data?.unique_follower || 0;

    const overlapData = [
        { name: "Total Followers", value: totalFollowers },
        { name: "Unique Followers", value: uniqueFollowers },
        {
            name: "Overlapping Followers",
            value: totalFollowers - uniqueFollowers,
        },
    ];

    const truncateString = (str, maxLength = 15) => {
        return str.length > maxLength
            ? str.substring(0, maxLength) + "..."
            : str;
    };

    const transformedData = data?.profiles?.map((profile) => ({
        username: profile.username,
        followers: profile.followers,
        unique_followers: profile.unique_followers,
        overlap_followers: profile.overlap_followers,
    }));

    const colors = [
        theme.palette.graph.cyan,
        theme.palette.graph.yellow,
        theme.palette.graph.magenta,
        theme.palette.graph.purple,
        theme.palette.graph.pink,
        theme.palette.graph.orange,
        theme.palette.graph.green,
    ];

    const formatValue = (value) => {
        if (value >= 1e7) return value / 1e7 + " Cr";
        if (value >= 1e5) return value / 1e5 + " L";
        if (value >= 1e3) return value / 1e3 + " K";
        return value.toString();
    };

    const horizontalBarOptions = {
        chart: {
            type: "bar",
            height: 400,
        },
        plotOptions: {
            bar: {
                horizontal: true,
                distributed: true,
                dataLabels: {
                    position: "right",
                },
                borderRadius: 0,
                barHeight: "40%", // Add this to control bar width
            },
        },
        stroke: {
            width: 2, // Decreased stroke width
        },
        dataLabels: {
            enabled: false,
        },
        xaxis: {
            categories: overlapData.map((item) => item.name),
            labels: {
                formatter: function (val) {
                    return formatValue(val);
                },
            },
            title: {
                // text: "Overview of Followers",
            },
            tickAmount: 6,
            min: 0,
            // max: 15e7, // Set maximum to 15 Cr
            axisTicks: {
                show: true,
            },
        },
        yaxis: {
            labels: {
                show: false,
            },
        },
        colors: [
            theme.palette.graph.cyan,
            theme.palette.graph.yellow,
            theme.palette.graph.magenta,
        ],
        title: {
            text: "Overview of Followers",
            align: "left",
        },
        grid: {
            xaxis: {
                lines: {
                    show: true,
                },
            },
            yaxis: {
                lines: {
                    show: false,
                },
            },
        },
        legend: {
            position: "bottom",
            horizontalAlign: "left",
        },
    };

    const barOptions = {
        chart: {
            type: "bar",
            height: 400,
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "45%",
                endingShape: "rounded",
            },
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            show: true,
            width: 2,
            colors: ["transparent"],
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            x: {
                show: true,
                formatter: function (value, { dataPointIndex }) {
                    // Show full username instead of truncated version
                    return data?.profiles[dataPointIndex]?.username || value;
                },
            },
            y: {
                formatter: function (value) {
                    return formatValue(value);
                },
            },
        },
        xaxis: {
            categories: transformedData.map((item) =>
                truncateString(item.username)
            ),
            labels: {
                // rotate: -45,
                // rotateAlways: true,
                // maxHeight: 100,
            },
        },
        yaxis: {
            title: {
                // text: "Number of Followers",
            },
            labels: {
                formatter: function (val) {
                    return formatValue(val);
                },
            },
        },
        fill: {
            opacity: 1,
        },
        colors: colors,
        title: {
            text: "Individual Followers Details",
            align: "left",
        },
    };

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <Typography variant="contentAccent" gutterBottom>
                        Overlap Followers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Add multiple usernames to compare their followers and
                        find overlapping followers
                    </Typography>
                </Box>
                <Button
                    startIcon={<EditIcon />}
                    variant="outlined"
                    onClick={onEdit}
                    sx={{ height: "fit-content", textTransform: "initial" }}
                >
                    Change
                </Button>
            </Box>
            <Box
                sx={{
                    my: 3,
                    borderBottom: "1px solid",
                    borderBottomColor: theme.palette.divider,
                    paddingBottom: "1rem",
                }}
            >
                <Typography variant="subtitle2">
                    Usernames ({data?.profiles?.length})
                </Typography>
                {data?.profiles.map((profile, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && ", "}
                        <Typography
                            variant="captionEmphasis"
                            component="span"
                            sx={{
                                color: theme.palette.neutral[500],
                            }}
                        >
                            {profile.username}
                        </Typography>
                    </React.Fragment>
                ))}
            </Box>
            <Typography variant="contentAccent" sx={{ mb: 3 }}>
                Results
            </Typography>
            <CardContainer>
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    <Box sx={{ width: "40%", pr: 2 }}>
                        <Card>
                            <ApexCharts
                                options={{
                                    ...horizontalBarOptions,
                                    chart: {
                                        ...horizontalBarOptions.chart,
                                        width: "100%",
                                        parentHeightOffset: 0,
                                        toolbar: {
                                            show: false,
                                        },
                                    },
                                    grid: {
                                        padding: {
                                            left: 0,
                                        },
                                    },
                                    tooltip: {
                                        enabled: true,
                                        shared: false,
                                        custom: function ({
                                            series,
                                            dataPointIndex,
                                        }) {
                                            const value =
                                                series[0][dataPointIndex];
                                            const name =
                                                overlapData[dataPointIndex]
                                                    .name;
                                            return (
                                                '<div class="apexcharts-tooltip-box">' +
                                                '<span class="apexcharts-tooltip-text">' +
                                                name +
                                                ": " +
                                                formatValue(value) +
                                                "</span></div>"
                                            );
                                        },
                                    },
                                }}
                                series={[
                                    {
                                        name: " ", // Empty name to remove "series" text
                                        data: overlapData.map(
                                            (item) => item.value
                                        ),
                                    },
                                ]}
                                type="bar"
                                height={400}
                            />
                        </Card>
                    </Box>
                    <Box sx={{ width: "60%" }}>
                        <Card>
                            <ApexCharts
                                options={{
                                    ...barOptions,
                                    chart: {
                                        ...barOptions.chart,
                                        width: "100%",
                                        parentHeightOffset: 0,
                                        toolbar: {
                                            show: false,
                                        },
                                    },
                                    grid: {
                                        padding: {
                                            left: 0,
                                            right: -50,
                                        },
                                    },
                                }}
                                series={[
                                    {
                                        name: "Total Followers",
                                        data: transformedData.map(
                                            (item) => item.followers
                                        ),
                                    },
                                    {
                                        name: "Unique Followers",
                                        data: transformedData.map(
                                            (item) => item.unique_followers
                                        ),
                                    },
                                    {
                                        name: "Overlapping Followers",
                                        data: transformedData.map(
                                            (item) => item.overlap_followers
                                        ),
                                    },
                                ]}
                                type="bar"
                                height={400}
                                width="100%"
                            />
                        </Card>
                    </Box>
                </Box>
            </CardContainer>
            {data?.ignored_profiles && data.ignored_profiles.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <span variant="body2" sx={{ fontStyle: "italic", mb: 1 }}>
                        For some reason the followers data is not available for
                        the following users that you have searched for:
                    </span>
                    {Object.entries(
                        data.ignored_profiles.reduce((acc, profile) => {
                            acc[profile.message] = acc[profile.message] || [];
                            acc[profile.message].push(profile.username);
                            return acc;
                        }, {})
                    ).map(([message, usernames]) => (
                        <span
                            key={message}
                            variant="body2"
                            // style={{
                            //     display: "flex",
                            //     alignItems: "center",
                            //     flexWrap: "wrap",
                            // }}
                        >
                            <strong style={{ marginRight: "0.5rem" }}>
                                {" "}
                                {usernames.join(", ")}
                            </strong>
                            <span>
                                There are many reasons like Privacy Settings,
                                Blocked or Suspended Accounts, Unlinked or
                                Inaccessible Accounts, Account Type or
                                Restrictions, Geographic Restrictions etc.
                            </span>
                        </span>
                    ))}
                </Alert>
            )}
        </Box>
    );
};

export default OverlapData;
